import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { MarketingTarotCard } from './MarketingTarotCard';
import { MARKETING_TAROT_DECK } from '@/data/marketingTarot';
import { X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const AllCardsView = ({ 
  isOpen, 
  onClose,
  selectionMode = false,
  singleSelect = false,
  onSelectionComplete,
  className 
}) => {
  const [selectedCards, setSelectedCards] = useState([]);
  const [shuffling, setShuffling] = useState(false);
  const [displayDeck, setDisplayDeck] = useState(MARKETING_TAROT_DECK);

  const maxCards = singleSelect ? 1 : 3;

  // Trigger shuffle animation on open if singleSelect
  React.useEffect(() => {
    if (isOpen && singleSelect) {
      setShuffling(true);
      setSelectedCards([]);
      const timer = setTimeout(() => {
        // Randomize deck order
        const shuffled = [...MARKETING_TAROT_DECK];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        setDisplayDeck(shuffled);
        setShuffling(false);
      }, 1200);
      return () => clearTimeout(timer);
    } else if (isOpen) {
      setDisplayDeck(MARKETING_TAROT_DECK);
    }
  }, [isOpen, singleSelect]);

  const handleCardClick = (card) => {
    if (!selectionMode || shuffling) return;
    
    const existingIndex = selectedCards.findIndex(c => c.id === card.id);
    
    if (existingIndex !== -1) {
      setSelectedCards(prev => prev.filter(c => c.id !== card.id));
    } else if (selectedCards.length < maxCards) {
      const cardWithReversed = {
        ...card,
        isReversed: Math.random() > 0.5
      };

      if (singleSelect) {
        // Single select: auto-confirm immediately
        setSelectedCards([cardWithReversed]);
        setTimeout(() => {
          if (onSelectionComplete) {
            onSelectionComplete([cardWithReversed]);
          }
          setSelectedCards([]);
        }, 600);
      } else {
        setSelectedCards(prev => [...prev, cardWithReversed]);
      }
    }
  };

  const handleConfirmSelection = () => {
    if (selectedCards.length === maxCards && onSelectionComplete) {
      onSelectionComplete(selectedCards);
      setSelectedCards([]);
    }
  };

  const handleClose = () => {
    setSelectedCards([]);
    onClose();
  };

  const getSelectionOrder = (cardId) => {
    const index = selectedCards.findIndex(c => c.id === cardId);
    return index !== -1 ? index + 1 : null;
  };

  if (!isOpen) return null;

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center",
      "bg-[#0a1628]/95 backdrop-blur-sm",
      className
    )}>
      {/* Close button */}
      <Button
        onClick={handleClose}
        variant="ghost"
        size="icon"
        className="absolute top-6 right-6 text-[#d4c4a8] hover:text-white hover:bg-[#d4c4a8]/20 z-10"
      >
        <X className="w-6 h-6"/>
      </Button>

      <div className="container mx-auto px-4 py-8 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="font-display text-3xl text-[#e8dcc8] mb-2">
            {singleSelect
              ? 'Draw Your Card'
              : selectionMode 
                ? 'Explore the Deck' 
                : `The ${MARKETING_TAROT_DECK.length} Marketing Archetypes`}
          </h2>
          <p className="text-[#d4c4a8]/60 font-body">
            {singleSelect
              ? (shuffling ? 'Shuffling the deck...' : 'Choose one card that calls to you')
              : selectionMode 
                ? 'Choose 3 cards to reveal your best hand'
                : 'Explore all cards in the Marketing Tarot deck'}
          </p>
          
          {/* Selection Progress - only for 3-card mode */}
          {selectionMode && !singleSelect && (
            <div className="flex items-center justify-center gap-3 mt-4">
              {[1, 2, 3].map((num) => (
                <div 
                  key={num}
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all",
                    selectedCards[num - 1] 
                      ? "border-[#e89b47] bg-[#e89b47]/20 text-[#e89b47]"
                      : "border-[#d4c4a8]/30 text-[#d4c4a8]/50"
                  )}
                >
                  {selectedCards[num - 1] ? (
                    <Check className="w-5 h-5"/>
                  ) : (
                    <span className="font-display text-sm">{num}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cards Display */}
        {shuffling ? (
          <div className="flex items-center justify-center py-20">
            <div className="relative">
              <div className="w-24 h-36 bg-gradient-to-br from-[#1e2a4a] to-[#0f1f35] border-2 border-[#e89b47]/40 rounded-xl animate-bounce shadow-lg shadow-[#e89b47]/20" />
              <div className="absolute top-2 left-2 w-24 h-36 bg-gradient-to-br from-[#1e2a4a] to-[#0f1f35] border-2 border-[#e89b47]/30 rounded-xl animate-bounce [animation-delay:150ms]" />
              <div className="absolute top-4 left-4 w-24 h-36 bg-gradient-to-br from-[#1e2a4a] to-[#0f1f35] border-2 border-[#e89b47]/20 rounded-xl animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        ) : singleSelect ? (
          /* Face-down fanned row for single card draw */
          <div className="flex items-center justify-center py-8 px-4 overflow-x-auto">
            <div className="relative flex items-end justify-center" style={{ height: '240px', width: '100%', maxWidth: '1100px' }}>
              {displayDeck.map((card, index) => {
                const total = displayDeck.length;
                const mid = (total - 1) / 2;
                const offset = index - mid;
                const rotation = offset * 3.5;
                const translateX = offset * 42;
                const translateY = Math.abs(offset) * 2.5;
                const isSelected = selectedCards.some(c => c.id === card.id);

                return (
                  <div
                    key={card.id}
                    className="absolute cursor-pointer transition-all duration-300 ease-out"
                    style={{
                      transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg)`,
                      zIndex: isSelected ? 50 : index,
                      bottom: '0px',
                    }}
                    onClick={() => handleCardClick(card)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = `translateX(${translateX}px) translateY(${translateY - 30}px) rotate(${rotation}deg) scale(1.12)`;
                      e.currentTarget.style.zIndex = '40';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg)`;
                        e.currentTarget.style.zIndex = String(index);
                      }
                    }}
                  >
                    {/* Face-down card */}
                    <div className={cn(
                      "w-[80px] h-[120px] rounded-xl border-2 shadow-lg",
                      "bg-gradient-to-br from-[#1a2d50] via-[#162340] to-[#0f1a30]",
                      "border-[#e89b47]/30 hover:border-[#e89b47]/70",
                      "hover:shadow-xl hover:shadow-[#e89b47]/20",
                      "transition-colors duration-300",
                      isSelected && "border-[#e89b47] shadow-[#e89b47]/30"
                    )}>
                      {/* Card back pattern */}
                      <div className="w-full h-full flex items-center justify-center rounded-lg overflow-hidden">
                        <div className="w-[60px] h-[100px] border border-[#e89b47]/20 rounded-lg flex items-center justify-center">
                          <div className="w-6 h-6 border border-[#e89b47]/30 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-[#e89b47]/40 rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 justify-items-center">
          {displayDeck.map((card) => {
            const selectionOrder = getSelectionOrder(card.id);
            const isSelected = selectionOrder !== null;
            
            return (
              <div 
                key={card.id}
                className={cn(
                  "flex flex-col items-center gap-3 group relative",
                  selectionMode && "cursor-pointer"
                )}
                onClick={() => handleCardClick(card)}
              >
                {/* Selection Badge */}
                {isSelected && (
                  <div className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-[#e89b47] flex items-center justify-center shadow-lg animate-scale-in">
                    <span className="font-display text-sm text-[#0a1628] font-bold">
                      {selectionOrder}
                    </span>
                  </div>
                )}
                
                <div className={cn(
                  "transform transition-all duration-300",
                  "group-hover:scale-105",
                  isSelected && "ring-4 ring-[#e89b47] rounded-xl scale-105",
                  selectionMode && !isSelected && selectedCards.length >= maxCards && "opacity-50"
                )}>
                  <MarketingTarotCard
                    card={card}
                    isFlipped={true}
                    size="default"
                    isHoverable={false}
                  />
                </div>
                
                {/* Hover keywords */}
                <div className={cn(
                  "text-center transition-opacity",
                  isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                )}>
                  <div className="flex flex-wrap justify-center gap-1 mt-1">
                    {card.keywords?.slice(0, 2).map((keyword, idx) => (
                      <span 
                        key={idx}
                        className="text-[#e89b47]/70 text-xs"
                      >
                        {keyword}{idx < 1 && ' •'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          {selectionMode && !singleSelect && (
            <>
              <Button
                onClick={() => setSelectedCards([])}
                variant="outline"
                className="bg-transparent border-[#d4c4a8]/40 text-[#d4c4a8] hover:bg-[#d4c4a8]/10"
                disabled={selectedCards.length === 0}
              >
                Clear Selection
              </Button>
              <Button
                onClick={handleConfirmSelection}
                className={cn(
                  "font-display tracking-wide transition-all",
                  selectedCards.length === 3
                    ? "bg-[#e89b47] text-[#0a1628] hover:bg-[#e89b47]/90"
                    : "bg-[#d4c4a8]/20 text-[#d4c4a8]/50 cursor-not-allowed"
                )}
                disabled={selectedCards.length !== maxCards}
              >
                <Check className="w-4 h-4 mr-2"/>
                Reveal My Cards ({selectedCards.length}/{maxCards})
              </Button>
            </>
          )}
        </div>

        {/* Close instruction */}
        <div className="text-center mt-6">
          <p className="text-[#d4c4a8]/40 text-sm">
            {singleSelect
              ? 'Tap a card to reveal your reading'
              : selectionMode 
                ? 'Select 3 cards then click Reveal, or press X to cancel'
                : 'Click X or press Escape to close'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default AllCardsView;
